import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ModReviewStatus =
  | "pending" | "in_review" | "resolved_keep" | "resolved_remove"
  | "resolved_warn" | "resolved_ban" | "escalated";

export interface ModReviewItem {
  id: string;
  subject_user_id: string | null;
  source_kind: string;
  source_ref: string | null;
  priority: number;
  status: ModReviewStatus;
  assigned_to: string | null;
  notes: string | null;
  resolution_note: string | null;
  created_at: string;
  updated_at: string;
}

export function useModReviewQueue(filter: ModReviewStatus | "all" = "pending") {
  const [items, setItems] = useState<ModReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from("mod_review_queue")
      .select("*")
      .order("priority", { ascending: true })
      .order("created_at", { ascending: true })
      .limit(50);
    if (filter !== "all") q = q.eq("status", filter);
    const { data, error } = await q;
    if (error) setError(error.message);
    setItems((data ?? []) as ModReviewItem[]);
    setLoading(false);
  }, [filter]);

  useEffect(() => { void refresh(); }, [refresh]);

  const resolve = useCallback(async (id: string, status: ModReviewStatus, note?: string) => {
    const { error } = await supabase
      .from("mod_review_queue")
      .update({
        status,
        resolution_note: note ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);
    if (!error) await refresh();
    return { error: error?.message ?? null };
  }, [refresh]);

  return { items, loading, error, resolve, refresh };
}
